<template>
  <div class="vp-rich-text">
    <StructuredRichText
      v-bind="$attrs"
      v-model="richTextValue"
      :input-id="inputId"
      :label="label"
      :placeholder="placeholder"
      :menu-items-config="menuItemCompactConfig"
      @mitem:click="onMenuItemActionClick"
    />
    <VPButton
      variant="outline"
      class="absolute bottom-0 right-6 ml-auto"
      size="xs"
      @click="toggleFullMode"
    >
      <ArrowExpand class="text-2xl" />
    </VPButton>
  </div>

  <Form
    class="vp-rich-text-full-section"
    :form-id="formId"
    :initial-values="initValues"
    @submit.prevent="() => {}"
  >
    <VPModal
      v-model:show="fullScreenModalShow"
      title="Full editor"
      size="lg"
    >
      <div class="mx-6">
        <StructuredRichText
          v-model="richTextValue"
          :input-id="computedInputIdForFullMode"
          :placeholder="placeholder"
          :menu-items-config="menuItemFullConfig"
          @mitem:click="onMenuItemActionClick"
        />
      </div>

      <template #commands="slotProps">
        <div class="flex justify-end mt-auto">
          <VPButton
            size="lg"
            variant="outline"
            max-width="8.75rem"
            @click.prevent="slotProps.close"
          >
            <ArrowCollapse class="text-2xl" />
          </VPButton>
        </div>
      </template>
    </VPModal>
    <VPModal
      v-model:show="embedCodeModalShow"
      title="Insert Embed"
      confirm-label="Insert"
      size="md"
      :z-index-level="2"
      :confirm-action-disabled="isInsertEmbedConfirmBtnDisabled"
      @confirm="onEmbedCodeModalConfirm"
    >
      <div class="flex flex-col mt-4">
        <VPDropdown
          input-id="embedTypeDropdown"
          label="Embed type:"
          :options="embedTypeOptions"
        />
      </div>
      <div class="my-3">
        <VPInput
          v-model="embedCode"
          class="rich-text-embed-code-input"
          label="Copy embed code:"
          input-id="embedCodeInput"
          :placeholder="embedPlaceholder"
        />
      </div>
    </VPModal>
    <VPModal
      v-model:show="mediaModalShow"
      :title="mediaModalTitle"
      size="sm"
      class="rich-text-media-modal"
      :z-index-level="2"
      confirm-label="Insert"
      :confirm-action-disabled="isInsertMediaConfirmBtnDisabled"
      @close="onMediaUrlModalClose"
      @backdrop-click="onMediaUrlModalClose"
      @confirm="onMediaUrlModalConfirm"
    >
      <VPInput
        v-model="mediaUrl"
        class="my-2"
        input-id="mediaUrlInput"
        placeholder="https://"
      />
    </VPModal>
  </Form>
</template>

<script setup lang="ts">
import { computed, ref, type Ref, inject, watch } from 'vue';
import { StructuredRichText, useFormsStore } from 'vuniform';
import VPInput from '@/components/form/VPInput.vue';
import VPButton from '@/components/form/VPButton.vue';
import VPModal from '@/components/form/VPModal.vue';
import VPDropdown from '@/components/form/VPDropdown.vue';
import { RichTextMenuItemEnum, RichTextEmbedTypeEnum, Form } from 'vuniform';
import { ArrowExpand, ArrowCollapse } from 'mdue';

const embedTypeOptions = [
  {
    name: 'Youtube',
    value: RichTextEmbedTypeEnum.YOUTUBE,
  },
  {
    name: 'Vimeo',
    value: RichTextEmbedTypeEnum.VIMEO,
  },
  {
    name: 'Spotify',
    value: RichTextEmbedTypeEnum.SPOTIFY,
  },
  {
    name: 'Bandcamp',
    value: RichTextEmbedTypeEnum.BANDCAMP,
  },
  {
    name: 'Custom',
    value: RichTextEmbedTypeEnum.CUSTOM,
  },
];

interface Props {
  label?: string;
  inputId: string;
  placeholder?: string;
  menuItemCompactConfig?: object[];
  menuItemFullConfig?: object[];
}
const props = withDefaults(defineProps<Props>(), {
  menuItemCompactConfig: () => defaultMenuItemCompactConfig,
  menuItemFullConfig: () => defaultMenuItemFullConfig,
});

const fullScreenModalShow = ref(false);
const embedCode = ref();
const embedCodeModalShow = ref(false);
const embedCodeAction: Ref<((...args: any[]) => void) | null> = ref(null);
const mediaUrl = ref('');
const mediaModalShow = ref(false);
const mediaUrlAction: Ref<((...args: any[]) => void) | null> = ref(null);
const mediaType = ref<RichTextMenuItemEnum | null>(null);

const initValues = ref({ embedTypeDropdown: 'youtube' });
const embedPlaceholder = ref('<iframe src="https://www.example.com" width="600" height="400"...');
const richTextValue = ref('');

const computedInputIdForFullMode = computed(() => `${props.inputId}-full`);
const computedRichTextValue = computed(() => richTextValue.value);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

watch(computedRichTextValue, newValue => {
  emit('update:modelValue', newValue);
});

const formsStore = useFormsStore();
const parentFormId = inject('formId', '');
const formId = 'rich-text-full-mode';

const toggleFullMode = () => {
  fullScreenModalShow.value = !fullScreenModalShow.value;
  const originalRichTextValue = formsStore.fieldGetValue(parentFormId, props.inputId);
  const values = { [computedInputIdForFullMode.value]: originalRichTextValue };
  formsStore.SET_INITIAL_VALUES({ values: values, formId });
};

const onEmbedCodeModalConfirm = () => {
  embedCodeModalShow.value = false;
  if (embedCodeAction.value) {
    embedCodeAction.value(embedCode.value);
    embedCode.value = '';
  }
};

const onMenuItemActionClick = (event: { type: RichTextMenuItemEnum; action: () => void }) => {
  if (event.type === RichTextMenuItemEnum.EMBED) {
    embedCodeModalShow.value = true;
    embedCodeAction.value = event.action;
  }
  if (
    event.type === RichTextMenuItemEnum.YOUTUBE ||
    event.type === RichTextMenuItemEnum.IMAGE ||
    event.type === RichTextMenuItemEnum.LINK
  ) {
    mediaModalShow.value = true;
    mediaUrlAction.value = event.action;
  }
  mediaType.value = event.type;
};

const isInsertEmbedConfirmBtnDisabled = computed(() => {
  return !embedCode.value;
});

const onMediaUrlModalClose = () => {
  mediaModalShow.value = false;
  mediaUrl.value = '';
  mediaType.value = null;
};

const onMediaUrlModalConfirm = () => {
  mediaModalShow.value = false;
  if (mediaUrlAction.value) {
    mediaUrlAction.value(mediaUrl.value);
    mediaUrl.value = '';
  }
  mediaType.value = null;
};
const mediaModalTitle = computed(() => {
  switch (mediaType.value) {
    case RichTextMenuItemEnum.YOUTUBE:
      return 'YouTube URL';
    case RichTextMenuItemEnum.IMAGE:
      return 'Image URL';
    case RichTextMenuItemEnum.LINK:
      return 'Link URL';
    default:
      return '';
  }
});

const isInsertMediaConfirmBtnDisabled = computed(() => {
  return !mediaUrl.value || !mediaUrl.value.match(/^(http|https):\/\/[^ "]+$/);
});
</script>

<script lang="ts">
const defaultMenuItemCompactConfig = [
  { type: RichTextMenuItemEnum.BOLD },
  { type: RichTextMenuItemEnum.ITALIC },
  { type: RichTextMenuItemEnum.STRIKE },
  { type: RichTextMenuItemEnum.LINK, manual: true },
  { type: RichTextMenuItemEnum.IMAGE, manual: true },
  { type: RichTextMenuItemEnum.EMBED, manual: true },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.HEADING_1 },
  { type: RichTextMenuItemEnum.HEADING_2 },
  { type: RichTextMenuItemEnum.PARAGRAPH },
  { type: RichTextMenuItemEnum.BULLET_LIST },
  { type: RichTextMenuItemEnum.ORDERED_LIST },
  { type: RichTextMenuItemEnum.ALIGN_LEFT },
  { type: RichTextMenuItemEnum.ALIGN_CENTER },
  { type: RichTextMenuItemEnum.ALIGN_RIGHT },
  { type: RichTextMenuItemEnum.ALIGN_JUSTIFY },
  { type: RichTextMenuItemEnum.BLOCKQUOTE },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.CLEAR_FORMAT },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.UNDO },
  { type: RichTextMenuItemEnum.REDO },
];

const defaultMenuItemFullConfig = [
  { type: RichTextMenuItemEnum.BOLD },
  { type: RichTextMenuItemEnum.ITALIC },
  { type: RichTextMenuItemEnum.STRIKE },
  { type: RichTextMenuItemEnum.UNDERLINE },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.COLOR },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.ALIGN_LEFT },
  { type: RichTextMenuItemEnum.ALIGN_CENTER },
  { type: RichTextMenuItemEnum.ALIGN_RIGHT },
  { type: RichTextMenuItemEnum.ALIGN_JUSTIFY },
  { type: RichTextMenuItemEnum.HORIZONTAL_RULE },
  { type: RichTextMenuItemEnum.HARD_BREAK },
  { type: RichTextMenuItemEnum.BLOCKQUOTE },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.LINK, manual: true },
  { type: RichTextMenuItemEnum.IMAGE, manual: true },
  { type: RichTextMenuItemEnum.EMBED, manual: true },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.HEADING_1 },
  { type: RichTextMenuItemEnum.HEADING_2 },
  { type: RichTextMenuItemEnum.PARAGRAPH },
  { type: RichTextMenuItemEnum.BULLET_LIST },
  { type: RichTextMenuItemEnum.ORDERED_LIST },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.CLEAR_FORMAT },
  { type: RichTextMenuItemEnum.DIVIDER },
  { type: RichTextMenuItemEnum.UNDO },
  { type: RichTextMenuItemEnum.REDO },
];
</script>
